import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormControl,
  ReactiveFormsModule,
  ValidationErrors,
  AbstractControl,
} from "@angular/forms";
import { AuthService } from "../../services/auth.service";
import { Router } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { catchError, Observable, of, tap } from "rxjs";
import { FieldsCacheService } from "@app/core/services/fields-cache.service";
import { ReferenceCacheService } from "@app/core/services/reference-cache.service";
import { HttpService } from "@app/core/services/http.service";
import { StorageService } from "@app/core/services/storage.service";
import { TokenResponse } from "@app/core/models/auth.model";
import { HttpErrorResponse, HttpResponse } from "@angular/common/http";
import { API_URLS } from "@app/core/config/url.config";
import { InputFieldComponent } from "@app/shared/components/input-field/input-field.component";
import { FaIconComponent } from "@fortawesome/angular-fontawesome";
import { NgClass } from "@angular/common";
import { MenuService } from "@app/core/layout/main-nav/services/menu.service";
import { ImageCacheService } from "@app/core/services/image-cache.service";
import { AppService } from "@app/core/services/app.service";
import { WorkingShiftService } from "@app/core/services/working-shift.service";

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    InputFieldComponent,
    FaIconComponent,
    NgClass,
  ],
  providers: [AuthService, Router],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss'
})

export class LoginComponent implements OnInit {

  loginForm!: FormGroup;
  changePasswordForm!: FormGroup;
  errorMessage: string = '';
  successMessage: string = '';
  mode: 'login' | 'changePassword' = 'login';

  constructor(
    private formBuilder: FormBuilder,
    private httpService: HttpService,
    private storageService: StorageService,
    private router: Router,
    private fieldsCacheService: FieldsCacheService,
    private referenceCacheService: ReferenceCacheService,
    private menuService: MenuService,
    private imageCacheService: ImageCacheService,
    private workingShiftService: WorkingShiftService
  ) {}

  ngOnInit(): void {
    this.loginForm = this.formBuilder.group({
      username: new FormControl('', [Validators.required]),
      password: new FormControl('', [Validators.required]),
    });

    this.changePasswordForm = this.formBuilder.group({
      username: new FormControl('', [Validators.required]),
      password: new FormControl('', [Validators.required, Validators.minLength(4)]),
      newPassword: new FormControl('', [
        Validators.required,
        Validators.minLength(4),
        this.passwordMatchValidator('password', true)
      ]),
      confirmPassword: new FormControl('', [
        Validators.required,
        Validators.minLength(4),
        this.passwordMatchValidator('newPassword')
      ]),
    });
  }

  onSubmit(): void {

    this.errorMessage = '';
    this.successMessage = '';

    if (this.mode === 'login' && this.loginForm.valid) {
      this.executeLogin(this.loginForm.value);
    } else if (this.mode === 'changePassword' && this.changePasswordForm.valid) {
      this.executeChangePassword(this.changePasswordForm.value);
    } else {
      this.mode === 'login' ? this.loginForm.markAllAsTouched() : this.changePasswordForm.markAllAsTouched();
    }
  }

  private executeLogin(formData: any): void {
    this.httpService.login(formData).pipe(
      tap((response) => this.handleLoginResponse(response)),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    ).subscribe();
  }

  private executeChangePassword(formData: any): void {
    this.httpService.changePassword(formData).pipe(
      tap((response) => this.handleChangePasswordResponse(response)),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    ).subscribe();
  }

  private handleLoginResponse(response: HttpResponse<any>): void {

    if (response.status === 200 && response.ok && response.body) {

      let loginUser: any;
      try {
        loginUser = JSON.parse(response.body);
      } catch (error) {
        loginUser = null;
      }

      const username = loginUser?.username;
      const lastUser = localStorage.getItem('lastLoggedInUser');

      if (username !== lastUser) {
        localStorage.removeItem('cardsTabs');
        localStorage.removeItem('originalPageUrl');
        localStorage.removeItem('lastActivity');
        localStorage.setItem('lastLoggedInUser', username);
      }

      this.clearCaches();

      if (response.url?.includes('change-password.jsp')) {
        this.mode = 'changePassword';
        const usernameMatch = response.url?.match(/username=([^&]*)/);
        if (usernameMatch && usernameMatch[1]) {
          const usernameFromUrl = usernameMatch[1];
          this.changePasswordForm.patchValue({ username : usernameFromUrl });
        }
      } else if (response.url === API_URLS.BASE_URL_LOGIN) {
        this.storeToken(response);
        this.navigateToOriginalUrl();
      } else if (response.url?.includes('error')) {
        this.errorMessage = 'Ошибка авторизации. Возможно, не ваша смена.';
      } else {
        this.errorMessage = 'Произошла неизвестная ошибка при авторизации.';
      }
    }
  }

  private handleChangePasswordResponse(response: HttpResponse<any>): void {
    if (response.status === 200 && response.ok) {
      if (response.url?.includes('login.jsp') || response.url === API_URLS.BASE_URL_MAIN) {
        this.mode = 'login';
        this.successMessage = 'Пароль успешно изменен. Пожалуйста, войдите с новым паролем.';
      } else if (response.url?.includes('change-password.jsp')) {
        const url = new URL(response.url);
        const params = new URLSearchParams(url.search);
        const error = params.get('error');

        if (error) {
          this.errorMessage = decodeURIComponent(error);
        }
      } else {
        this.errorMessage = 'Произошла неизвестная ошибка при изменении пароля.';
      }
    } else {
      this.errorMessage = 'Ошибка сервера. Пожалуйста, повторите попытку позже.';
    }
  }

  private handleError(error: HttpErrorResponse): Observable<void> {
    setTimeout(() => {
      if (error.status === 401) {
        this.errorMessage = 'Неправильный логин или пароль.';
        console.error(error.message);
      } else {
        this.errorMessage = 'Произошла ошибка при аутентификации. Пожалуйста, повторите попытку позже.';
        console.error(error.message);
      }
    }, 300);

    return of();
  }

  private storeToken(response: HttpResponse<string>): void {
    try {
      if (!response.body) {
        return;
      }
      const tokenResponse: TokenResponse = JSON.parse(response.body);
      this.storageService.setToken(tokenResponse);
      const token = response.headers.get('X-Csrf-Token');
      if (token) {
        localStorage.setItem('X-CSRF-TOKEN', token);
      }
    } catch (error) {
      console.error('Ошибка при получении токена:', error);
    }
  }

  passwordMatchValidator(matchTo: string, notEqual: boolean = false): (control: AbstractControl) => ValidationErrors | null {
    return (control: AbstractControl): ValidationErrors | null => {
      if (this.changePasswordForm) {
        const matchControl = this.changePasswordForm.get(matchTo);
        if (notEqual) {
          return matchControl && control.value === matchControl.value ? { notDifferent: true } : null;
        }
        return matchControl && control.value !== matchControl.value ? { mismatch: true } : null;
      }
      return null;
    };
  }

  private navigateToOriginalUrl(): void {
    const originalUrl = localStorage.getItem('originalPageUrl') || '/';
    this.router.navigateByUrl(originalUrl).then(() => {
      window.location.reload();
    });
  }

  private clearCaches(): void {
    this.fieldsCacheService.clearCache().subscribe();
    this.referenceCacheService.clearCache().subscribe();
    this.menuService.clearCache().subscribe();
    this.imageCacheService.clearCache().subscribe();
  }
}
